import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, type AvailableIntegration, type IntegrationSubscriptionView } from "../api";
import { useApiData } from "../hooks";
import { useApp } from "../store";
import { Empty, ErrorNote, Header, Loading, Note, SectionLabel } from "../ui";

/**
 * Integration settings: enable an administrator-defined integration for one number and supply the token
 * the external system issued. The token goes out with every push so the recipient can verify it is for
 * them; it is write-only, so it is never displayed back.
 */
export function IntegrationsPage(): JSX.Element {
  const { numbers } = useApp();
  const [params, setParams] = useSearchParams();
  const selectable = numbers.filter((number) => number.state !== "RELEASED");
  const numberId = params.get("numberId") ?? selectable[0]?.id ?? "";

  const { data: available, error: availableError } = useApiData<AvailableIntegration[]>("/api/v1/integrations");
  const { data: enabled, error: enabledError, reload } = useApiData<IntegrationSubscriptionView[]>(
    numberId ? `/api/v1/integrations/numbers/${numberId}` : null
  );

  const [tokens, setTokens] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<unknown>(null);

  useEffect(() => {
    setTokens({});
  }, [numberId]);

  async function toggle(integrationId: string, currentlyEnabled: boolean): Promise<void> {
    setActionError(null);
    try {
      if (currentlyEnabled) {
        await api.delete(`/api/v1/integrations/numbers/${numberId}/${integrationId}`);
      } else {
        const token = tokens[integrationId]?.trim();
        if (!token) {
          throw new Error("Enter the token from the external system first");
        }
        await api.put(`/api/v1/integrations/numbers/${numberId}/${integrationId}`, {
          recipientToken: token
        });
      }
      reload();
    } catch (caught) {
      setActionError(caught);
    }
  }

  const stateOf = (integrationId: string): IntegrationSubscriptionView | undefined =>
    (enabled ?? []).find((subscription) => subscription.integrationId === integrationId);

  return (
    <>
      <Header title="Integrations" />
      <div className="content">
        <div className="field">
          <label htmlFor="numberSelect">Configure for number</label>
          <select
            id="numberSelect"
            value={numberId}
            onChange={(event) => setParams({ numberId: event.target.value })}
          >
            {selectable.map((number) => (
              <option key={number.id} value={number.id}>
                {number.e164}
              </option>
            ))}
          </select>
        </div>
        <Note>
          Only administrator-defined integrations are available. Enable them per number and provide the
          token from the external system; it is included on each outbound push so the recipient can verify
          it.
        </Note>
        <ErrorNote error={availableError} />
        <ErrorNote error={enabledError} />
        <ErrorNote error={actionError} />

        <SectionLabel>Available integrations</SectionLabel>
        {available === null ? (
          <Loading />
        ) : available.length === 0 ? (
          <Empty>No integrations have been published.</Empty>
        ) : (
          <ul className="list">
            {available.map((integration) => {
              const subscription = stateOf(integration.id);
              const isEnabled = subscription?.enabled ?? false;
              return (
                <li key={integration.id}>
                  <div className="avatar sq">{integration.name.slice(0, 2).toUpperCase()}</div>
                  <div className="row-main">
                    <div className="row-title">{integration.name}</div>
                    <div className="row-sub">
                      {isEnabled
                        ? `Enabled \u2014 token ${subscription?.tokenSet ? "set" : "missing"}`
                        : "Disabled"}
                    </div>
                    <div className="row-sub">
                      {integration.eventScope.toLowerCase().replace(/_/g, " ")} {"\u00B7"}{" "}
                      {integration.authScheme.toLowerCase().replace(/_/g, " ")}
                    </div>
                    {!isEnabled ? (
                      <div className="field" style={{ marginTop: 8, marginBottom: 0 }}>
                        <input
                          type="text"
                          placeholder="Token from external system"
                          value={tokens[integration.id] ?? ""}
                          onChange={(event) =>
                            setTokens({ ...tokens, [integration.id]: event.target.value })
                          }
                        />
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className={isEnabled ? "toggle on" : "toggle"}
                    aria-label={isEnabled ? "Disable" : "Enable"}
                    onClick={() => toggle(integration.id, isEnabled)}
                  />
                </li>
              );
            })}
          </ul>
        )}
        <Note>
          Outbound only: the backend pushes call and message metadata to the external system. Integrations
          never send into the app, and they are silent while a number is disabled.
        </Note>
      </div>
    </>
  );
}
